<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue'
  import { Separator } from '@/components/ui/separator'
  import { RouterLink, useRoute } from 'vue-router'
  import { ApplicationConfiguration } from '@/types'
  import configService from '@/services/configService'
  import { Subject } from 'rxjs'
  import { debounceTime } from 'rxjs/operators'
  import { Search, X } from 'lucide-vue-next'

  const appConfig = ref<ApplicationConfiguration | null>(null)

  // Define a TypeScript interface for the button objects
  interface Button {
    icon: any // Replace 'any' with a more specific type if you have one (e.g., Component)
    onClick?: () => void
    animate?: boolean // animate the button
    animationIcon?: any // a different icon to use during animation
    title?: string
    toolTip?: string
    href?: string
    environments?: string[]
    id?: string
  }

  // Define props for the component
  const props = defineProps<{
    showSearch?: boolean
    onSearch?: (value: string) => void
    buttons?: Button[]
    messages?: string[]
    searchValue?: string // Add search value prop
  }>()

  // Provide default values if props are not passed in
  const showSearch = props.showSearch ?? false
  const buttons = props.buttons ?? []
  const onSearch = props.onSearch ?? ((_value: string) => {})
  const route = useRoute()

  const searchValue = ref<string>(props.searchValue ?? '')
  const searchSubject = new Subject<string>()
  const searchSubscription = searchSubject.pipe(debounceTime(500)).subscribe((value) => {
    onSearch(value)
  })

  // Handle search input changes
  function handleSearch(event: Event) {
    searchValue.value = (event.target as HTMLInputElement).value
    searchSubject.next(searchValue.value)
  }

  async function handleButtonClick(btn: Button) {
    if (btn.onClick) {
      btn.onClick()
    }
  }

  // This function returns true if the button's route matches the current route.
  function isActiveRoute(path: string): boolean {
    return route.path === path
  }

  function shouldDisplayButton(btn: Button): boolean {
    if (btn.environments && btn.environments.length > 0 && appConfig?.value?.environment?.name) {
      return btn.environments.includes(appConfig?.value?.environment?.name)
    }
    return true
  }

  function clearSearch(): void {
    searchValue.value = ''
    onSearch(searchValue.value)
  }

  onMounted(async () => {
    await configService.loadApplicationConfig()
    appConfig.value = configService.getApplicationConfigData()
  })

  onUnmounted(() => {
    searchSubscription.unsubscribe()
    searchSubject.complete()
  })
</script>

<template>
  <nav
    class="app-menu-bar flex h-16 w-full items-center justify-between rounded-lg border px-6 text-white"
  >
    <!-- Left Side: Button Group -->
    <div class="flex items-center space-x-4">
      <template v-for="btn in buttons">
        <component
          v-if="shouldDisplayButton(btn)"
          :is="btn.href ? RouterLink : 'button'"
          :to="btn.href"
          :id="btn.id"
          @click="handleButtonClick(btn)"
          :class="[
            // If a title exists, use extra horizontal padding for the text.
            btn.title
              ? 'small-button hover:bg-gray-10 flex items-center rounded border px-4 py-2 shadow'
              : 'small-button hover:bg-gray-10 flex h-10 w-10 items-center justify-center rounded border shadow',
            // Active state styling.
            btn.href && isActiveRoute(btn.href) ? 'bg-primary text-white' : 'hover:bg-primary'
          ]"
        >
          <a-tooltip :title="btn.toolTip" placement="bottom">
            <component
              :is="btn.animate && btn.animationIcon ? btn.animationIcon : btn.icon"
              :dummy="btn.animate"
              class="h-6 w-6 select-none focus:outline-none"
              :style="{ userSelect: 'none' }"
              :class="{ 'animate-spin': btn.animate }"
              draggable="false"
              unselectable="on"
            />
          </a-tooltip>
          <!-- Conditionally render the title text -->
          <span v-if="btn.title" class="ml-2">{{ btn.title }}</span>
        </component>
      </template>
    </div>

    <!-- Center: Custom Content Slot -->
    <div class="flex items-center">
      <slot name="center-content" />
    </div>

    <!-- Right Side: Search Input (only rendered if showSearch is true) -->
    <div class="flex items-center" v-if="showSearch">
      <div class="flex h-full items-center">
        <a-input-search
          placeholder="Search local"
          :value="searchValue"
          size="large"
          style="min-width: 150px"
          @input="handleSearch"
          class="search-input-aligned"
        >
          <template #enterButton>
            <a-button @click="searchValue ? clearSearch() : undefined">
              <X v-if="searchValue?.length" class="h-4 w-4" />
              <Search v-else class="h-4 w-4" />
            </a-button>
          </template>
        </a-input-search>
      </div>
    </div>
  </nav>
  <div class="mb-2"></div>
  <div class="w-full">
    <div class="flex justify-end text-xs text-muted-foreground">
      <div v-for="message in messages">
        {{ message }}
      </div>
    </div>
  </div>
  <Separator class="my-6" />
</template>

<style>
  .ant-btn-default {
    box-shadow: none !important;
  }
  .no-select {
    user-select: none;
  }

  .search-input-aligned .ant-input-search-button {
    display: flex !important;
    justify-content: center !important;
    align-items: center;
    height: 40px;
  }

  .search-input-aligned input.ant-input {
    height: 40px;
  }

  .ant-input-group-addon {
    background-color: var(--background) !important;
  }
</style>
